import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Layer 2 anomaly: a CONSTANT cell whose value is an Excel error literal
 * (#N/A, #REF!, #VALUE!, #DIV/0!, etc. — the full set is in the sink's
 * isErrorLiteral helper).
 *
 * This nearly always means someone pasted an evaluated #N/A or copy/
 * pasted as values over a formula that was returning #N/A — the error
 * becomes a literal in the cell. Subsequent recalcs can't fix it
 * because there's no formula left. Almost always a paste accident
 * and almost always a real bug.
 *
 * Fires regardless of the band's dominant origin — a hardcoded error
 * is wrong in any context.
 */
export const hardcodedErrorLiteral: AuditRule = {
  id: "system.audit.hardcoded-error-literal",
  name: "Hardcoded error literal",
  description:
    "A cell containing an Excel error value as a constant (no formula). " +
    "Almost always a paste accident — pasting values from a cell that was " +
    "evaluating to #N/A / #REF! / etc., losing the formula that could fix it.",
  enabled: true,
  source: "system",
  severity: "error",
  needsPatternAnalysis: true,
  check(sheet) {
    if (!sheet.patternAnalysis) return [];
    const findings: AuditFindingPartial[] = [];
    const { startRow, startCol } = sheet.bounds;

    for (const a of sheet.patternAnalysis.anomalies) {
      if (a.type !== "hardcoded_error") continue;
      findings.push({
        sheetName: sheet.name,
        address: addressFromRC(startRow + a.row, startCol + a.col),
        message:
          "cell contains an Excel error value as a hardcoded constant — likely paste accident",
      });
    }
    return findings;
  },
};
